import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatCurrency, formatDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import Layout from "../components/Layout";
import TransactionDetailModal from "../components/TransactionDetailModal";
import {
  ChevronLeft, Building2, MapPin, Trash2, ArrowRightLeft, History,
  Calendar, AlertCircle, Landmark, TrendingUp, Wrench, FileText,
  DollarSign, CheckCircle2, Plus, Clock, Eye, Upload, Paperclip, Camera,
  ShieldCheck, TrendingDown
} from "lucide-react";

import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid
} from "recharts";
import { toast } from "sonner";
import { Field, SelectField } from "./Properties";
import HelpTip from "../components/Help/HelpTip";


export default function PropertyDetail() {
  const { id } = useParams();
  const { can } = useAuth();
  const { isDark } = useTheme();
  const [p, setP] = useState(null);
  const [txs, setTxs] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedTx, setSelectedTx] = useState(null);
  const [activeTab, setActiveTab] = useState("overview"); // overview, mortgage, valuations, maintenance, improvements, documents

  // Feature: Capital Improvements for property
  const [improvements, setImprovements] = useState([]);


  // Feature 3: Mortgage Schedule
  const [mortgageData, setMortgageData] = useState(null);
  const [postingMortgage, setPostingMortgage] = useState(false);

  // Feature 4: Valuations
  const [valuations, setValuations] = useState([]);
  const [showValuationModal, setShowValuationModal] = useState(false);
  const [valForm, setValForm] = useState({
    valuation_date: new Date().toISOString().slice(0, 10),
    estimated_value: "",
    source: "appraisal",
    notes: "",
  });

  // Feature 2: Maintenance for property
  const [maintenanceList, setMaintenanceList] = useState([]);

  // Feature 6: Documents for property
  const [docList, setDocList] = useState([]);

  // Strategy conversion modal
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertForm, setConvertForm] = useState({
    to_type: "STR",
    conversion_date: new Date().toISOString().slice(0, 10),
    personal_use_days: 0,
    notes: "",
  });

  const navigate = useNavigate();

  const loadAll = useCallback(async () => {
    try {
      const [pRes, tRes, bRes, mRes, vRes, maintRes, docRes, ciRes] = await Promise.all([
        api.get(`/properties/${id}`),
        api.get(`/transactions?property_id=${id}`),
        api.get(`/bookings?property_id=${id}`),
        api.get(`/properties/${id}/mortgage-schedule`),
        api.get(`/properties/${id}/valuations`),
        api.get(`/maintenance?property_id=${id}`),
        api.get(`/documents?property_id=${id}`),
        api.get(`/capital-improvements?property_id=${id}`).catch(() => ({ data: [] })),
      ]);
      setP(pRes.data);
      setTxs(tRes.data || []);
      setBookings(bRes.data || []);
      setMortgageData(mRes.data);
      setValuations(vRes.data || []);
      setMaintenanceList(maintRes.data || []);
      setDocList(docRes.data || []);
      setImprovements(ciRes?.data || []);
    } catch (err) {
      console.error("Failed to load property details:", err);
    }
  }, [id]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const [uploadingCover, setUploadingCover] = useState(false);
  const coverInputRef = useRef(null);

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Photo exceeds 15MB limit");
      return;
    }
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/properties/upload-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.patch(`/properties/${id}`, { image_url: res.data.image_url });
      toast.success("Cover photo updated");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo update failed");
    } finally {
      setUploadingCover(false);
    }
  };

  const handleConvert = async (e) => {

    e.preventDefault();
    try {
      await api.post(`/properties/${id}/conversions`, {
        from_type: p.leasing_type,
        to_type: convertForm.to_type,
        conversion_date: convertForm.conversion_date,
        personal_use_days: parseInt(convertForm.personal_use_days) || 0,
        notes: convertForm.notes,
      });
      toast.success(`Property converted to ${convertForm.to_type}`);
      setShowConvertModal(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to convert strategy");
    }
  };

  const handlePostMortgagePayment = async () => {
    if (!window.confirm(`Post monthly mortgage payment of ${formatCurrency(p.monthly_mortgage_payment)} to transactions?`)) return;
    setPostingMortgage(true);
    try {
      await api.post(`/properties/${id}/post-mortgage-payment`, {
        amount: p.monthly_mortgage_payment,
      });
      toast.success("Mortgage payment posted to Books!");
      loadAll();
    } catch (err) {
      toast.error("Failed to post mortgage payment");
    } finally {
      setPostingMortgage(false);
    }
  };

  const handleAddValuation = async (e) => {
    e.preventDefault();
    if (!valForm.estimated_value) return;
    try {
      await api.post(`/properties/${id}/valuations`, {
        ...valForm,
        estimated_value: parseFloat(valForm.estimated_value),
      });
      toast.success("Valuation benchmark saved");
      setShowValuationModal(false);
      setValForm({
        valuation_date: new Date().toISOString().slice(0, 10),
        estimated_value: "",
        source: "appraisal",
        notes: "",
      });
      loadAll();
    } catch (err) {
      toast.error("Failed to save valuation");
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this property and all linked data?")) return;
    await api.delete(`/properties/${id}`);
    toast.success("Property deleted");
    navigate("/properties");
  };

  if (!p) return <Layout title="Property" subtitle="Loading…"><div className="card p-12 skeleton" /></Layout>;

  // Chart data for valuation history
  const valuationChartData = valuations.map((v) => ({
    date: v.valuation_date,
    value: v.estimated_value,
    source: v.source,
  }));

  // Initial purchase point if not in valuations
  if (valuationChartData.length > 0 && p.purchase_date && p.purchase_price) {
    if (!valuationChartData.some((v) => v.date === p.purchase_date)) {
      valuationChartData.unshift({
        date: p.purchase_date,
        value: p.purchase_price,
        source: "purchase",
      });
      valuationChartData.sort((a, b) => new Date(a.date) - new Date(b.date));
    }
  }

  const latestValuation = valuations.length > 0 ? valuations[valuations.length - 1].estimated_value : (p.purchase_price || 0);
  const appreciationAmount = latestValuation - (p.purchase_price || 0);
  const appreciationPct = p.purchase_price > 0 ? ((appreciationAmount / p.purchase_price) * 100).toFixed(1) : 0;

  return (
    <Layout
      title={p.name}
      subtitle={p.address}
      actions={
        <div className="flex items-center gap-2">
          {can.edit && (
            <button
              onClick={() => {
                setConvertForm({
                  to_type: p.leasing_type === "LTR" ? "STR" : "LTR",
                  conversion_date: new Date().toISOString().slice(0, 10),
                  personal_use_days: p.personal_use_days || 0,
                  notes: "",
                });
                setShowConvertModal(true);
              }}
              data-testid="convert-strategy-btn"
              className="btn-secondary text-xs py-2 px-3"
            >
              <ArrowRightLeft size={14} /> Convert Strategy
            </button>
          )}
          <button
            onClick={() => navigate(-1)}
            data-testid="back-btn"
            className="p-2 rounded-xl border border-[var(--border)] text-[var(--muted)] hover:text-[var(--text)]"
          >
            <ChevronLeft size={18} />
          </button>
        </div>
      }
    >
      {/* Property Hero Banner */}
      <div className="card overflow-hidden mb-6">
        <div className="aspect-[21/9] sm:aspect-[24/8] bg-gradient-to-br from-[#1C3A5E] to-[#0D9488] relative overflow-hidden">
          {p.image_url ? (
            <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/30 font-black text-6xl select-none">
              {p.name.slice(0, 2).toUpperCase()}
            </div>
          )}
          <div className="absolute top-4 left-4 flex items-center gap-2">
            <span className="tag bg-black/60 text-white backdrop-blur-md">{p.leasing_type}</span>
            {p.entity_name && (
              <span className="tag bg-teal-950/80 text-teal-300 border border-teal-500/40 backdrop-blur-md">
                {p.entity_name}
              </span>
            )}
          </div>

          {can.edit && (
            <div className="absolute bottom-3 right-3">
              <button
                type="button"
                onClick={() => coverInputRef.current?.click()}
                disabled={uploadingCover}
                className="p-2 px-3 rounded-xl bg-black/60 hover:bg-black/80 text-white backdrop-blur-md text-xs font-semibold flex items-center gap-1.5 border border-white/20 shadow-lg transition-colors"
                title="Upload / Change Cover Photo"
              >
                <Camera size={14} />
                <span>{uploadingCover ? "Uploading..." : "Change Photo"}</span>
              </button>
              <input
                type="file"
                ref={coverInputRef}
                onChange={handleCoverUpload}
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
              />
            </div>
          )}
        </div>


        <div className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight">{p.name}</h2>
            <div className="text-sm text-[var(--muted)] flex items-center gap-1.5 mt-1">
              <MapPin size={15} className="text-teal-400" />
              <span>{[p.address, p.city, p.state, p.zip].filter(Boolean).join(", ")}</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {can.delete && (
              <button
                onClick={remove}
                data-testid="delete-prop-btn"
                className="p-2 rounded-xl text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/20 text-xs font-semibold flex items-center gap-1.5 transition-colors"
              >
                <Trash2 size={15} /> Delete Property
              </button>
            )}
          </div>
        </div>

        {/* Property Navigation Tabs */}
        <div className="border-t border-[var(--border)]">
          <nav className="px-6 flex items-center gap-6 overflow-x-auto no-scrollbar -mb-px" role="tablist" aria-label="Property Sections">
            {[
              { id: "overview", label: "Overview", icon: Building2 },
              { id: "mortgage", label: "Mortgage & Loan", icon: Landmark },
              { id: "valuations", label: "Valuation History", icon: TrendingUp },
              { id: "improvements", label: "Improvements", count: improvements.length, icon: ShieldCheck },
              { id: "maintenance", label: "Maintenance", count: maintenanceList.length, icon: Wrench },
              { id: "documents", label: "Documents", count: docList.length, icon: FileText },
            ].map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={activeTab === t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setActiveTab(t.id)}
                className={`py-3.5 border-b-2 text-xs sm:text-sm font-semibold flex items-center gap-2 transition-all whitespace-nowrap shrink-0 ${
                  activeTab === t.id
                    ? "border-teal-500 text-teal-400"
                    : "border-transparent text-[var(--muted)] hover:text-[var(--text)] hover:border-[var(--border)]"
                }`}
              >
                <t.icon size={16} className="shrink-0" />
                <span>{t.label}</span>
                {t.count !== undefined && (
                  <span
                    className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold tabular-nums transition-colors ${
                      activeTab === t.id
                        ? "bg-teal-500/20 text-teal-300"
                        : "bg-slate-200/80 dark:bg-slate-800 text-[var(--muted)]"
                    }`}
                  >
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Strategy Conversion Modal */}
      {showConvertModal && (
        <div className="sheet-backdrop" onClick={() => setShowConvertModal(false)}>
          <div className="sheet-content" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-[var(--border)] pb-4 mb-4">
              <div className="flex items-center gap-2 font-bold text-lg">
                <ArrowRightLeft size={18} className="text-teal-400" /> Convert Strategy (Mid-Year)
              </div>
              <button onClick={() => setShowConvertModal(false)} className="text-xs text-[var(--muted)]">Close</button>
            </div>
            <p className="text-xs text-[var(--muted)] mb-4">
              Mid-year conversions require splitting rental periods for IRS Schedule E tax compliance. Income and expenses before conversion will report under {p.leasing_type}.
            </p>
            <form onSubmit={handleConvert} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SelectField
                label="New Strategy"
                value={convertForm.to_type}
                onChange={(v) => setConvertForm({ ...convertForm, to_type: v })}
                options={["LTR", "STR", "Hybrid"].filter((t) => t !== p.leasing_type)}
              />
              <Field
                label="Conversion Date"
                type="date"
                value={convertForm.conversion_date}
                onChange={(v) => setConvertForm({ ...convertForm, conversion_date: v })}
                required
              />
              <Field
                label="Personal Use Days (IRS § 280A)"
                type="number"
                value={convertForm.personal_use_days}
                onChange={(v) => setConvertForm({ ...convertForm, personal_use_days: v })}
              />
              <Field
                label="Reason / Notes"
                value={convertForm.notes}
                onChange={(v) => setConvertForm({ ...convertForm, notes: v })}
              />
              <div className="sm:col-span-2 flex justify-end gap-3 mt-4 pt-3 border-t border-[var(--border)]">
                <button type="button" onClick={() => setShowConvertModal(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">Confirm Conversion</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* TAB 1: OVERVIEW */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          {/* Metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Income MTD" value={formatCurrency(p.income_mtd)} accent="text-emerald-400" />
            <Stat label="Expense MTD" value={formatCurrency(p.expense_mtd)} />
            <Stat label="NOI MTD" value={formatCurrency(p.noi_mtd)} accent={p.noi_mtd >= 0 ? "text-emerald-400" : "text-red-400"} helpTopic="noi" />
            <Stat label="Mortgage Payment" value={formatCurrency(p.monthly_mortgage_payment) + "/mo"} helpTopic="mortgage-tracker" />
          </div>


          {/* Tenant Card if LTR */}
          {p.tenant && (
            <div className="card p-5">
              <div className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold mb-2">
                Active Long-Term Tenant
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-bold text-base">{p.tenant.name}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 font-medium">
                    Rent: {formatCurrency(p.tenant.rent_amount)}/mo · Due day {p.tenant.rent_due_day || 1}
                  </div>
                </div>
                {p.lease_days_remaining !== null && (
                  <span className="tag tag-ltr">
                    {p.lease_days_remaining > 0 ? `${p.lease_days_remaining} days left` : "Lease Expired"}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Transactions for Property */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-base">Transaction Ledger</h3>
              <span className="text-xs text-slate-600 dark:text-slate-400 font-medium">{txs.length} entries</span>
            </div>
            {txs.length === 0 ? (
              <div className="text-xs text-slate-600 dark:text-slate-400 text-center py-6">No transactions recorded for this property.</div>
            ) : (
              <div className="divide-y divide-[var(--border)] max-h-96 overflow-y-auto">
                {txs.map((t) => (
                  <div
                    key={t.id}
                    onClick={() => setSelectedTx(t)}
                    className="py-3 px-2 flex items-center justify-between text-xs hover:bg-[var(--surface-hover)] rounded-xl transition-colors cursor-pointer group"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedTx(t);
                      }
                    }}
                  >
                    <div>
                      <div className="font-semibold text-sm flex items-center gap-2">
                        <span className="group-hover:text-[var(--text)] transition-colors">{t.category}</span>
                        {t.receipt_path && (
                          <span className="text-[10px] text-teal-400 bg-teal-500/10 border border-teal-500/30 px-1.5 py-0.5 rounded font-bold flex items-center gap-1">
                            <Paperclip size={10} /> Receipt
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 font-medium">{formatDate(t.date)} {t.notes ? `· ${t.notes}` : ""}</div>
                    </div>
                    <div className={`font-bold tabular-nums text-sm ${t.type === "Income" ? "text-emerald-400" : "text-[var(--text)]"}`}>
                      {t.type === "Income" ? "+" : "−"}{formatCurrency(t.amount)}
                    </div>
                  </div>
                ))}
              </div>

            )}
          </div>
        </div>
      )}

      {/* TAB 2: MORTGAGE & LOAN TRACKER */}
      {activeTab === "mortgage" && (
        <div className="space-y-6">
          {/* Mortgage Summary KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="glass-card p-4">
              <div className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold flex items-center justify-between">
                <span>Current Loan Balance</span>
                <HelpTip topic="mortgage-balance" />
              </div>
              <div className="text-2xl font-black tabular-nums mt-1 text-teal-400">
                {mortgageData ? formatCurrency(mortgageData.current_balance) : "—"}
              </div>
              <div className="text-xs text-[var(--muted)] mt-0.5">
                Principal: {formatCurrency(p.mortgage_amount || 0)}
              </div>
            </div>

            <div className="glass-card p-4">
              <div className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold flex items-center justify-between">
                <span>Estimated Net Equity</span>
                <HelpTip topic="equity" />
              </div>
              <div className="text-2xl font-black tabular-nums mt-1 text-emerald-400">
                {mortgageData ? formatCurrency(mortgageData.equity) : "—"}
              </div>
              <div className="text-xs text-[var(--muted)] mt-0.5">
                {mortgageData?.equity_percentage || 0}% of Market Value
              </div>
            </div>


            <div className="glass-card p-4">
              <div className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold">
                Monthly P&I Payment
              </div>
              <div className="text-2xl font-black tabular-nums mt-1">
                {mortgageData ? formatCurrency(mortgageData.monthly_payment) : "—"}
              </div>
              <div className="text-xs text-[var(--muted)] mt-0.5">
                Rate: {p.interest_rate || 0}% · {p.loan_term_years || 30} Yrs
              </div>
            </div>

            <div className="glass-card p-4">
              <div className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold">
                Estimated Payoff Date
              </div>
              <div className="text-2xl font-black tabular-nums mt-1">
                {mortgageData?.payoff_date ? mortgageData.payoff_date.slice(0, 7) : "—"}
              </div>
              <div className="text-xs text-[var(--muted)] mt-0.5">
                {mortgageData?.months_elapsed || 0} mos elapsed
              </div>
            </div>
          </div>

          {/* Auto-Post Mortgage Payment Action Card */}
          <div className="card p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-teal-500/30 bg-teal-500/5">
            <div>
              <h3 className="font-bold text-base flex items-center gap-2">
                <Landmark size={18} className="text-teal-400" /> Auto-Post Monthly Mortgage Payment
              </h3>
              <p className="text-xs text-[var(--muted)] mt-1 max-w-xl">
                Records a monthly mortgage transaction in your ledger under <strong>Mortgage Interest</strong>.
                Current split: <strong className="text-teal-300">Principal: ${mortgageData?.current_month_principal?.toFixed(2) || "0.00"}</strong> and <strong className="text-teal-300">Interest: ${mortgageData?.current_month_interest?.toFixed(2) || "0.00"}</strong>.
              </p>
            </div>
            {can.edit && (
              <button
                onClick={handlePostMortgagePayment}
                disabled={postingMortgage}
                className="btn-primary shrink-0"
              >
                <CheckCircle2 size={16} />
                <span>{postingMortgage ? "Posting..." : "Post Payment Now"}</span>
              </button>
            )}
          </div>

          {/* Amortization Schedule Table */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-base">Annual Amortization Schedule</h3>
                <p className="text-xs text-[var(--muted)] mt-0.5">Year-by-year principal reduction & interest paid</p>
              </div>
              <div className="text-xs text-[var(--muted)]">
                Total Interest over loan: <strong>{formatCurrency(mortgageData?.total_interest_over_life || 0)}</strong>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-[var(--border)] text-[var(--muted)] uppercase tracking-wider text-[10px]">
                    <th className="py-2.5 px-3">Year</th>
                    <th className="py-2.5 px-3">Start Balance</th>
                    <th className="py-2.5 px-3">Principal Paid</th>
                    <th className="py-2.5 px-3">Interest Paid</th>
                    <th className="py-2.5 px-3">End Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {(mortgageData?.yearly_schedule || []).map((row) => (
                    <tr key={row.year} className="hover:bg-[var(--surface-hover)]">
                      <td className="py-2.5 px-3 font-bold">Year {row.year}</td>
                      <td className="py-2.5 px-3 tabular-nums">{formatCurrency(row.beginning_balance)}</td>
                      <td className="py-2.5 px-3 tabular-nums text-emerald-400 font-semibold">{formatCurrency(row.principal_paid)}</td>
                      <td className="py-2.5 px-3 tabular-nums text-amber-400">{formatCurrency(row.interest_paid)}</td>
                      <td className="py-2.5 px-3 tabular-nums font-bold text-teal-400">{formatCurrency(row.ending_balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: VALUATION HISTORY */}
      {activeTab === "valuations" && (
        <div className="space-y-6">
          {/* Valuation KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Current Valuation" value={formatCurrency(latestValuation)} accent="text-teal-400" />
            <Stat label="Original Purchase" value={formatCurrency(p.purchase_price)} />
            <Stat label="Total Appreciation" value={formatCurrency(appreciationAmount)} accent="text-emerald-400" />
            <Stat label="Appreciation Return" value={`${appreciationPct}%`} accent="text-emerald-400" />
          </div>

          {/* Action Header */}
          <div className="flex justify-end">
            {can.edit && (
              <button
                onClick={() => setShowValuationModal(true)}
                className="btn-primary text-xs py-2 px-3.5"
              >
                <Plus size={14} /> Log New Valuation Benchmark
              </button>
            )}
          </div>

          {/* Appreciation Line Chart */}
          <div className="card p-5">
            <h3 className="font-bold text-base mb-1">Asset Value Over Time</h3>
            <p className="text-xs text-[var(--muted)] mb-4">Historical property appreciation and appraisal timeline</p>

            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={valuationChartData} margin={{ top: 10, right: 15, left: 15, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"} />
                  <XAxis dataKey="date" stroke={isDark ? "#94a3b8" : "#475569"} fontSize={11} tickLine={false} />
                  <YAxis stroke={isDark ? "#94a3b8" : "#475569"} fontSize={11} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: isDark ? "rgba(19, 19, 26, 0.95)" : "rgba(255, 255, 255, 0.95)",
                      border: isDark ? "1px solid #1E1E2E" : "1px solid #E2E8F0",
                      borderRadius: "12px",
                      color: isDark ? "#F3F4F6" : "#0F172A",
                      fontSize: "12px",
                      boxShadow: isDark ? "0 10px 25px rgba(0,0,0,0.5)" : "0 10px 25px rgba(0,0,0,0.08)",
                    }}
                    formatter={(v) => [`$${Number(v).toLocaleString()}`, "Estimated Value"]}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#0D9488"
                    strokeWidth={3}
                    dot={{ fill: "#0D9488", r: 4 }}
                    activeDot={{ r: 6, fill: "#10B981" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Valuation Entries List */}
          <div className="card p-5">
            <h3 className="font-bold text-base mb-3">Historical Valuation Records</h3>
            {valuations.length === 0 ? (
              <div className="text-center py-6 text-xs text-[var(--muted)]">
                No appraisals or valuations logged yet. Click above to log your first valuation benchmark.
              </div>
            ) : (
              <div className="divide-y divide-[var(--border)]">
                {valuations.map((v) => (
                  <div key={v.id} className="py-3 flex items-center justify-between text-xs">
                    <div>
                      <div className="font-bold text-sm tabular-nums text-teal-400">
                        {formatCurrency(v.estimated_value)}
                      </div>
                      <div className="text-[var(--muted)] mt-0.5 flex items-center gap-2">
                        <span>Date: {v.valuation_date}</span>
                        <span>·</span>
                        <span className="capitalize">Source: {v.source}</span>
                        {v.notes && <span>· "{v.notes}"</span>}
                      </div>
                    </div>
                    <span className="tag tag-muted">{v.source}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 4: MAINTENANCE REQUESTS */}
      {activeTab === "maintenance" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-base">Property Maintenance Orders</h3>
            <button
              onClick={() => navigate("/maintenance")}
              className="btn-secondary text-xs py-1.5 px-3"
            >
              Open Full Maintenance Center →
            </button>
          </div>

          {maintenanceList.length === 0 ? (
            <div className="card p-8 text-center text-xs text-[var(--muted)]">
              No maintenance requests currently open for this property.
            </div>
          ) : (
            <div className="space-y-2.5">
              {maintenanceList.map((m) => (
                <div key={m.id} className="card p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`tag ${m.priority === "urgent" ? "bg-red-500/20 text-red-400" : "tag-muted"}`}>
                        {m.priority}
                      </span>
                      <span className="font-bold text-sm truncate">{m.title}</span>
                    </div>
                    <div className="text-xs text-[var(--muted)]">
                      Status: <strong className="capitalize text-[var(--text)]">{m.status}</strong> · Est: ${m.estimated_cost || 0}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate("/maintenance")}
                    className="btn-secondary text-xs py-1 px-2.5 shrink-0"
                  >
                    View
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TAB 5: PROPERTY DOCUMENTS */}
      {activeTab === "documents" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-base">Vaulted Property Documents</h3>
            <button
              onClick={() => navigate("/documents")}
              className="btn-secondary text-xs py-1.5 px-3"
            >
              Vault New Document →
            </button>
          </div>

          {docList.length === 0 ? (
            <div className="card p-8 text-center text-xs text-[var(--muted)]">
              No documents vaulted specifically for this property.
            </div>
          ) : (
            <div className="space-y-2.5">
              {docList.map((d) => (
                <div key={d.id} className="card p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText size={16} className="text-teal-400 shrink-0" />
                      <span className="font-bold text-sm truncate">{d.name}</span>
                      <span className="tag tag-muted">{d.category}</span>
                    </div>
                    <div className="text-xs text-[var(--muted)]">
                      Uploaded {new Date(d.uploaded_at).toLocaleDateString()}
                    </div>
                  </div>
                  <a
                    href={`/api/documents/${d.id}/file`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-secondary text-xs py-1.5 px-3 shrink-0 flex items-center gap-1"
                  >
                    <Eye size={13} /> View
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TAB 6: CAPITAL IMPROVEMENTS */}
      {activeTab === "improvements" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h3 className="font-bold text-base">Capital Improvements & Tax Basis</h3>
              <p className="text-xs text-[var(--muted)]">
                Depreciable capital assets, additions, and cost-segregated improvements for this property.
              </p>
            </div>
            <button
              onClick={() => navigate("/improvements")}
              className="btn-primary text-xs py-2 px-3.5 flex items-center gap-1.5 self-start sm:self-auto"
            >
              <Plus size={14} /> Add Improvement
            </button>
          </div>

          {/* Basis Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="glass-card p-4">
              <div className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold">Original Purchase Price</div>
              <div className="text-xl font-bold tabular-nums text-[var(--text)] mt-1">{formatCurrency(p?.purchase_price || 0)}</div>
              <div className="text-[10px] text-[var(--muted)] mt-0.5">Initial tax acquisition basis</div>
            </div>
            <div className="glass-card p-4">
              <div className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold">Total Improvements</div>
              <div className="text-xl font-bold tabular-nums text-teal-400 mt-1">
                {formatCurrency(improvements.reduce((sum, i) => sum + (Number(i.cost) || 0), 0))}
              </div>
              <div className="text-[10px] text-[var(--muted)] mt-0.5">{improvements.length} capitalized asset{improvements.length === 1 ? "" : "s"}</div>
            </div>
            <div className="glass-card p-4">
              <div className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold">Accumulated Depreciation</div>
              <div className="text-xl font-bold tabular-nums text-amber-400 mt-1">
                {formatCurrency(improvements.reduce((sum, i) => sum + (Number(i.total_depreciation_to_date) || 0), 0))}
              </div>
              <div className="text-[10px] text-[var(--muted)] mt-0.5">Total depreciation claimed to date</div>
            </div>
            <div className="glass-card p-4 border border-teal-500/30 bg-teal-500/5">
              <div className="text-[11px] uppercase tracking-wider text-teal-400 font-semibold">Adjusted Tax Basis</div>
              <div className="text-xl font-bold tabular-nums text-teal-300 mt-1">
                {formatCurrency(
                  (Number(p?.purchase_price) || 0) +
                  improvements.reduce((sum, i) => sum + (Number(i.cost) || 0), 0) -
                  improvements.reduce((sum, i) => sum + (Number(i.total_depreciation_to_date) || 0), 0)
                )}
              </div>
              <div className="text-[10px] text-teal-400/70 mt-0.5">Basis + Improvements - Depr</div>
            </div>
          </div>

          {/* Schedule Table */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-bold text-sm">Depreciable Assets Schedule</h4>
              <button
                onClick={() => navigate("/improvements")}
                className="text-xs text-teal-400 hover:underline"
              >
                Open Full Asset Ledger →
              </button>
            </div>

            {improvements.length === 0 ? (
              <div className="text-center py-8 text-xs text-[var(--muted)]">
                No capital improvements recorded for this property yet.
                <div className="mt-3">
                  <button
                    onClick={() => navigate("/improvements")}
                    className="btn-secondary text-xs py-1.5 px-3 inline-flex items-center gap-1.5"
                  >
                    <Plus size={13} /> Log First Improvement
                  </button>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-[var(--border)] text-[var(--muted)] uppercase tracking-wider text-[10px]">
                      <th className="py-2.5 px-2">Asset / Description</th>
                      <th className="py-2.5 px-2">Category</th>
                      <th className="py-2.5 px-2">In Service</th>
                      <th className="py-2.5 px-2 text-right">Cost</th>
                      <th className="py-2.5 px-2 text-right">Recovery</th>
                      <th className="py-2.5 px-2 text-right">Annual Depr.</th>
                      <th className="py-2.5 px-2 text-right">Total Depr.</th>
                      <th className="py-2.5 px-2 text-right">Remaining Basis</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border)]">
                    {improvements.map((imp) => (
                      <tr key={imp.id} className="hover:bg-white/[0.02]">
                        <td className="py-3 px-2">
                          <div className="font-semibold text-[var(--text)]">{imp.description}</div>
                          {imp.notes && <div className="text-[10px] text-[var(--muted)] truncate max-w-xs">{imp.notes}</div>}
                        </td>
                        <td className="py-3 px-2 capitalize text-[var(--muted)]">{imp.category}</td>
                        <td className="py-3 px-2 tabular-nums text-[var(--muted)]">{imp.date_placed_in_service}</td>
                        <td className="py-3 px-2 text-right font-semibold tabular-nums text-[var(--text)]">
                          {formatCurrency(imp.cost)}
                        </td>
                        <td className="py-3 px-2 text-right tabular-nums text-[var(--muted)]">
                          {imp.recovery_period_years} yrs
                        </td>
                        <td className="py-3 px-2 text-right tabular-nums text-teal-400">
                          {formatCurrency(imp.annual_depreciation || 0)}/yr
                        </td>
                        <td className="py-3 px-2 text-right tabular-nums text-amber-400">
                          {formatCurrency(imp.total_depreciation_to_date || 0)}
                        </td>
                        <td className="py-3 px-2 text-right tabular-nums font-semibold text-[var(--text)]">
                          {formatCurrency(imp.remaining_basis != null ? imp.remaining_basis : (imp.cost - (imp.total_depreciation_to_date || 0)))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Log Valuation Modal */}
      {showValuationModal && (
        <div className="sheet-backdrop" onClick={() => setShowValuationModal(false)}>
          <div className="sheet-content" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-[var(--border)] pb-4 mb-4">
              <h2 className="text-lg font-bold">Log Valuation Benchmark</h2>
              <button onClick={() => setShowValuationModal(false)} className="text-xs text-[var(--muted)]">Close</button>
            </div>
            <form onSubmit={handleAddValuation} className="space-y-4">
              <div>
                <label className="label">Estimated Market Value ($) *</label>
                <input
                  type="number"
                  step="1000"
                  required
                  placeholder="e.g. 485000"
                  value={valForm.estimated_value}
                  onChange={(e) => setValForm({ ...valForm, estimated_value: e.target.value })}
                  className="lapen-input tabular-nums font-bold text-base"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Valuation Date *</label>
                  <input
                    type="date"
                    required
                    value={valForm.valuation_date}
                    onChange={(e) => setValForm({ ...valForm, valuation_date: e.target.value })}
                    className="lapen-input"
                  />
                </div>
                <div>
                  <label className="label">Source</label>
                  <select
                    value={valForm.source}
                    onChange={(e) => setValForm({ ...valForm, source: e.target.value })}
                    className="lapen-input"
                  >
                    <option value="appraisal">Formal Appraisal</option>
                    <option value="cma">Realtor CMA</option>
                    <option value="tax_assessment">County Tax Assessment</option>
                    <option value="automated">Zillow / Redfin AVM</option>
                    <option value="refinance">Bank Refinance</option>
                    <option value="manual">Manual Estimate</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="label">Notes</label>
                <input
                  type="text"
                  placeholder="e.g. Post-renovation appraisal for cash-out refi"
                  value={valForm.notes}
                  onChange={(e) => setValForm({ ...valForm, notes: e.target.value })}
                  className="lapen-input"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-[var(--border)]">
                <button type="button" onClick={() => setShowValuationModal(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">Save Valuation</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedTx && (
        <TransactionDetailModal
          transaction={selectedTx}
          onClose={() => setSelectedTx(null)}
          onUpdate={(updated) => {
            setTxs((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
            setSelectedTx(updated);
          }}
        />
      )}

    </Layout>
  );
}


function Stat({ label, value, accent, testId, helpTopic }) {
  return (
    <div className="glass-card p-4" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-slate-600 dark:text-slate-400 font-semibold flex items-center justify-between">
        <span>{label}</span>
        {helpTopic && <HelpTip topic={helpTopic} />}
      </div>
      <div className={`text-xl font-bold tracking-tight tabular-nums mt-1 ${accent || ""}`}>{value}</div>
    </div>
  );
}

